import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import kc_v from '../kc_v.jpg'; // Background image
import api from '../services/api'; // Import the api instance
import Captcha from './Captcha';
import '../styles/login.css';

const Login = ({ updateAuth }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaResetTrigger, setCaptchaResetTrigger] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({ username: '', password: '' });
  const navigate = useNavigate();
  const usernameRef = useRef(null);

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Helper function to check if token is valid
  const isTokenValid = (token) => {
    if (!token) return false;
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return false;
      const payload = JSON.parse(atob(tokenParts[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp && payload.exp > currentTime;
    } catch (error) {
      return false;
    }
  };

  // Check if user is already authenticated on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    
    if (token && role) {
      if (isTokenValid(token)) {
        // Valid token, redirect to dashboard
        navigate('/');
      } else {
        // Expired token, clear it
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        localStorage.removeItem('username');
      }
    } else {
      // Auto-focus username field when component mounts
      setTimeout(() => {
        usernameRef.current?.focus();
      }, 100);
    }
  }, [navigate]);

  // Validate form fields
  const validateForm = () => {
    const newErrors = {};
    if (!username.trim()) {
      newErrors.username = 'Username/Email is required';
    }
    if (!password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Validate form fields
    if (!validateForm()) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }
    
    if (!captchaVerified) {
      setSnackbar({ open: true, message: 'Please verify the CAPTCHA first', severity: 'error' });
      return;
    }

    try {
      setLoading(true);
      setErrors({ username: '', password: '' }); // Clear previous errors
      
      const res = await api.post('/auth/login', { username, password });
      console.log('Login response:', res.data);

      const role = res.data.role.trim().toLowerCase();
      updateAuth(res.data.token, role, username);

      setSnackbar({ open: true, message: 'Login successful!', severity: 'success' });

      setTimeout(() => {
        navigate('/'); // Redirect to home page
      }, 1000);
    } catch (error) {
      console.error('Login error:', error.response ? error.response.data : error.message);
      
      // Get specific error message from backend
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Login failed. Please check your credentials and try again.';
      
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      
      // Clear password field on error for security
      setPassword('');
      
      // Reset captcha on authentication failure
      setCaptchaResetTrigger(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && captchaVerified) {
      handleLogin(e);
    }
  };

  return (
    <Box
      className="login-page"
      sx={{
        '&::before': {
          backgroundImage: `url(${kc_v})`
        }
      }}
    >
      <Box
        component="form"
        onSubmit={handleLogin}
        className="login-form"
      >
        <Box className="login-title">
          <Typography 
            variant="h4" 
            component="h1"
          >
            Welcome Back
          </Typography>
        </Box>
        <TextField
          fullWidth
          label="Username/Email"
          variant="outlined"
          value={username}
          inputRef={usernameRef}
          onChange={(e) => {
            setUsername(e.target.value);
            if (errors.username) setErrors({ ...errors, username: '' });
          }}
          onBlur={() => {
            if (!username.trim()) {
              setErrors({ ...errors, username: 'Username/Email is required' });
            }
          }}
          error={!!errors.username}
          helperText={errors.username}
          required
          disabled={loading}
          autoComplete="username"
          aria-label="Username or Email"
          FormHelperTextProps={{
            sx: {
              backgroundColor: errors.username ? 'rgba(211, 47, 47, 0.1)' : 'transparent',
              padding: errors.username ? '4px 8px' : 0,
              borderRadius: errors.username ? '4px' : 0,
              fontWeight: errors.username ? 500 : 400,
              marginTop: errors.username ? 1 : 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }
          }}
        />
        <TextField
          fullWidth
          label="Password"
          type={showPassword ? 'text' : 'password'}
          variant="outlined"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (errors.password) setErrors({ ...errors, password: '' });
          }}
          onBlur={() => {
            if (!password) {
              setErrors({ ...errors, password: 'Password is required' });
            }
          }}
          onKeyPress={handleKeyPress}
          error={!!errors.password}
          helperText={errors.password}
          required
          disabled={loading}
          autoComplete="current-password"
          aria-label="Password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  disabled={loading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="password-toggle-button"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
          FormHelperTextProps={{
            sx: {
              backgroundColor: errors.password ? 'rgba(211, 47, 47, 0.1)' : 'transparent',
              padding: errors.password ? '4px 8px' : 0,
              borderRadius: errors.password ? '4px' : 0,
              fontWeight: errors.password ? 500 : 400,
              marginTop: errors.password ? 1 : 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }
          }}
        />
        
        <Box className={`captcha-container ${loading ? 'loading' : ''}`}>
          <Captcha onVerify={setCaptchaVerified} resetTrigger={captchaResetTrigger} />
        </Box>
        
        <Button 
          fullWidth 
          variant="contained" 
          type="submit"
          disabled={loading || !captchaVerified}
          startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
          className={`login-button ${loading ? 'loading' : ''}`}
        >
          {loading ? 'Signing In...' : 'Sign In'}
        </Button>

        {/* Forget Password Link */}
        <Box className="forgot-password-link">
          <Button 
            onClick={() => navigate('/forget-password')}
            disabled={loading}
          >
            Forgot your password?
          </Button>
        </Box>
        
        {/* Loading Overlay */}
        {loading && (
          <Box className="loading-overlay">
            <CircularProgress size={40} />
          </Box>
        )}
      </Box>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={snackbar.severity === 'error' ? 5000 : 3000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          role="alert"
          aria-live="polite"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login;
